import * as countries from "i18n-iso-countries";
import { reactive, replace } from "vanjs-ext";

countries.registerLocale(require("i18n-iso-countries/langs/de.json"));
countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
countries.registerLocale(require("i18n-iso-countries/langs/fr.json"));
countries.registerLocale(require("i18n-iso-countries/langs/sv.json"));

// This is temporarily used because the backend will in some cases send all
// available translations and let the frontend pick which one to use.
// The backend will be updated to select based on the language header and only
// return the selected translation at which point this function will no longer
// be needed.
export const customTranslation = (dict: Record<Locale, string>): string => {
	return dict[currentLocale];
};

export const getCountryNames = (): countries.LocalizedCountryNames<{
	select: "official";
}> => {
	return countries.getNames(currentLocale.substring(0, 2));
};

export const getWeekdays = (): string[] => {
	return [
		t.sunday,
		t.monday,
		t.tuesday,
		t.wednesday,
		t.thursday,
		t.friday,
		t.saturday,
	];
};

export const dateString = (dateLike: DateLike): string => {
	const date = parseDateLike(dateLike);
	return date.toLocaleDateString(currentLocale);
};

export const dateTimeString = (dateLike: DateLike): string => {
	const date = parseDateLike(dateLike);
	return `${dateString(date)} ${date.toLocaleTimeString(currentLocale)}`;
};

export const kitchenTimeString = (dateLike: DateLike): string => {
	const date = parseDateLike(dateLike);
	const hour = date.getHours().toString().padStart(2, "0");
	const minute = date.getMinutes().toString().padStart(2, "0");
	return `${hour}:${minute}`;
};

export const relativeTimeString = (dateLike: DateLike): string => {
	const date = parseDateLike(dateLike);
	const now = new Date();
	const elapsed = date.getTime() - now.getTime();
	const absElapsed = Math.abs(elapsed);

	const second = 1000;
	const minute = second * 60;
	const hour = minute * 60;
	const day = hour * 24;
	const month = day * 30;
	const year = month * 12;

	const rtf = new Intl.RelativeTimeFormat(currentLocale, {
		numeric: "auto",
	});

	if (absElapsed < minute)
		return rtf.format(Math.round(elapsed / second), "second");

	if (absElapsed < hour)
		return rtf.format(Math.round(elapsed / minute), "minute");

	if (absElapsed < day) return rtf.format(Math.round(elapsed / hour), "hour");

	if (absElapsed < month) return rtf.format(Math.round(elapsed / day), "day");

	if (absElapsed < year)
		return rtf.format(Math.round(elapsed / month), "month");

	return rtf.format(Math.round(elapsed / year), "year");
};

const parseDateLike = (dateLike: DateLike): Date => {
	const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
	if (Number.isNaN(date.getTime())) {
		throw new Error("invalid date");
	}
	return date;
};

export const setLocale = (locale: Locale): void => {
	const translation = translations[locale];
	if (!translation) {
		throw new Error(`missing locale: ${locale}`);
	}
	replace(t, translation);
	document.documentElement.lang = currentLocale.substring(0, 2);
};

const enGB = {
	account: "Account",
	accountCreationStep1: "Step 1: Enter the required account details.",
	accountCreationStep2:
		"Step 2 (optional): Add additional information to be included on your invoices. You may skip this step and add the details later via the billing menu.",
	accountDeletionPage: {
		disclaimer:
			"By clicking confirm you agree to cancelling all active subscriptions owned by this account without a refund and the immediate deletion of all data associated with the account. Note that some data, such as data regarding financial transactions, may be kept in accordance with currently applicable law.",
		header: "Confirm account deletion",
	},
	add: "Add",
	addressLine1: "Address line 1",
	addressLine2: "Address line 2",
	appRedirectionMessage: "You are being redirected to the app",
	back: "Back",
	billing: "Billing",
	buySms: "Buy SMS",
	cancel: "Cancel",
	confirm: "Confirm",
	confirmPurchase: "Confirm purchase",
	connectionErrorPage: {
		body: "Connection to the server failed. Please verify your internet connection and try again. If you are still experiencing issues, you can contact our support at https://support.luda.farm.",
		tryAgainButton: "Try again",
	},
	country: "Country",
	create: "Create",
	createAccount: "Create account",
	day: "Day",
	days: "Days",
	deleteAccount: "Delete account",
	device: {
		addDevicePage: {
			chooseNamePrompt: "Choose a name for your device",
			selectDevicePrompt: "Select the device you wish to add",
		},
		card: {
			connectionWarning: "Connection lost",
			planLabel: "Plan",
			subscriptionStatusEnded: "Ended",
			subscriptionStatusEnds: "Ends",
			subscriptionStatusRenews: "Renews",
			statusLabel: "Device status",
		},
		label: "Device",
		logsPage: {
			timeIntervalLabel: "Time interval",
			untilLabel: "Until",
		},
		settingsPage: {
			deviceRemovalDisclaimer:
				"By clicking confirm you acknowledge that you wish to remove this device from your account and that any remaining subscription time or other paid for features associated with this device will be lost without compensation with immediate effect.",
			removeDeviceLabel: "Remove device",
			sharingLabel: "Sharing",
		},
		subscriptionLabel: "Subscription",
	},
	devices: "Devices",
	done: "Done",
	edit: "Edit",
	email: "Email",
	export: "Export",
	farmcamSim: {
		addDevicePage: {
			header: "Add FarmCam SIM",
			scanImsiBarcodePrompt:
				"Scan the IMSI barcode. If the barcode is not recognised, enter the IMSI manually.",
		},
		card: {
			lowDataWarning: "Low data",
			outOfDataWarning: "Out of data",
		},
		remainingDataLabel: "Remaining data",
		settingsPage: {
			buyExtraDataButton: "Buy extra data",
			confirmExtraDataPurchaseHeader: "Confirm purchase of extra data",
			extraDataPurchaseDisclaimer: (price: string): string =>
				`Confirm your purchase of 1 GB additional data for ${price} ex VAT`,
		},
	},
	fencealarm: {
		addDevicePage: {
			header: "Add FenceAlarm",
			scanImeiBarcodePrompt:
				"Scan the IMEI barcode. If the barcode is not recognised, enter the IMEI manually.",
		},
		alarmLevelLabel: "Alarm level",
		card: {
			batteryLevelLabel: "Battery level",
			lowBatteryWarning: "Low battery voltage",
			lowVoltageWarning: "Low fence voltage",
		},
		settingsPage: {
			calibrationLabel: "Calibration",
			expectedVoltageLabel: "Expected voltage",
			voltageAlarmLabel: "Voltage alarm",
		},
	},
	fencealarm2: {
		addDevicePage: {
			header: "Add FenceAlarm2",
			scanImeiQrPrompt:
				"Scan the IMEI QR-code. If the QR-code is not recognised, enter the IMEI manually.",
		},
	},
	friday: "Friday",
	language: "Language",
	lastActive: "Last active",
	latestVersion: "Latest version",
	menu: "Menu",
	monday: "Monday",
	monthly: "Monthly",
	name: "Name",
	newsletter: "Newsletter",
	newsletterUrl: "https://www.luda.farm/pages/newsletter",
	none: "None",
	notifications: "Notifications",
	off: "Off",
	on: "On",
	owner: "Owner",
	pageNotFound: "Page not found",
	password: "Password",
	passwordResetStep1:
		"Step 1: Request a verification code. If you cannot find the code in your inbox, please check your spam folder. Requesting a new verification code will invalidate any previously issued codes.",
	passwordResetStep2:
		"Step 2: Enter the verification code and choose a new password.",
	phone: "Phone",
	postalCode: "Postal code",
	price: {
		monthly: (amount: number, currency: Currency): string =>
			`${amount} ${currency} per month ex VAT`,
		yearly: (amount: number, currency: Currency): string =>
			`${amount} ${currency} per year ex VAT`,
	},
	remove: "Remove",
	reset: "Reset",
	resetPassword: "Reset password",
	saturday: "Saturday",
	save: "Save",
	sendVerificationCode: "Send verification code",
	signalStrength: "Signal strength",
	signIn: "Sign in",
	signOut: "Sign out",
	smartplug: {
		addDevicePage: {
			header: "Add SmartPlug",
			scanImeiBarcodePrompt:
				"Scan the IMEI barcode. If the barcode is not recognised, enter the IMEI manually.",
		},
		card: {
			consumptionWarning: "Consumption has exceeded threshold",
			highLoadWarning: "High load",
			highTempWarning: "High temperature",
			lowLoadWarning: "Low load",
			lowTempWarning: "Low temperature",
			powerFailureWarning: "Power failure",
			powerLabel: "Power",
		},
		consumptionLabel: "Consumption",
		powerLoadLabel: "Power load",
		settingsPage: {
			automation: {
				actionLabel: "Action",
				turnOff: "Turn off",
				turnOn: "Turn on",
			},
			automationActionLabel: "Action",
			consumptionLabel: "Consumption",
			deviceBeepLabel: "Device beep",
			highestExpectedLoadLabel: "Highest acceptable load (max 3700 W)",
			highestExpectedTempLabel:
				"Highest acceptable temperature (max 70 °C)",
			lowestExpectedLoadLabel: "Lowest acceptable load (min 0 W)",
			lowestExpectedTempLabel:
				"Lowest acceptable temperature (min -20 °C)",
			powerFailureNotificationsLabel: "Power failure notifications",
			powerLoadNotificationsLabel: "Power load notifications",
			temperatureNotifications: "Temperature notifications",
			timer: {
				label: "Timer",
				newRuleLabel: "New timer rule",
			},
			thermostat: {
				above: "Above",
				below: "Below",
				label: "Thermostat",
				newRuleLabel: "New thermostat rule",
				temperatureLabel: "Temperature (-20 - 70 °C)",
			},
		},
		temperatureLabel: "Temperature",
	},
	smartplug2: {
		addDevicePage: {
			header: "Add SmartPlug2",
			scanImeiQrPrompt:
				"Scan the IMEI QR-code. If the QR-code is not recognised, enter the IMEI manually.",
		},
	},
	smsPurchaseDisclaimer: (amount: number, currency: Currency): string =>
		`By clicking confirm you agree to the purchase and immediate delivery of 100 SMS for receiving notifications. The amount to be paid is ${amount} ${currency} ex VAT and will be included on your next invoice.`,
	sunday: "Sunday",
	supportUrl: "https://support.luda.farm/en/support/solutions/14000076272",
	tags: "Tags",
	thursday: "Thursday",
	time: "Time",
	town: "Town",
	trial: "Trial",
	tuesday: "Tuesday",
	update: "Update",
	vatId: "EU VAT identification number",
	verificationCode: "Verification code",
	verificationCodeInstruction:
		"If you cannot find the verification code email in your inbox, please check your spam folder. Requesting a new verification code will invalidate all previously issued codes.",
	verifyAccount: "Verify account",
	voltage: "Voltage",
	wednesday: "Wednesday",
	yearly: "Yearly",
};

export const currentLocale: Locale = "en-GB";
export const t: TranslationSchema = reactive(enGB);
const translations: Record<Locale, TranslationSchema> = {
	"en-GB": enGB,
	"de-DE": {
		account: "Konto",
		accountCreationStep1:
			"Schritt 1: Geben Sie die erforderlichen Kontodaten ein.",
		accountCreationStep2:
			"Schritt 2 (optional): Fügen Sie zusätzliche Informationen für Ihre Rechnungen hinzu. Sie können diesen Schritt überspringen und die Daten später über das Rechnungsmenü hinzufügen.",
		accountDeletionPage: {
			disclaimer:
				"Durch Klicken auf Bestätigen stimmen Sie der Stornierung aller aktiven Abonnements dieses Kontos ohne Rückerstattung und der sofortigen Löschung aller mit dem Konto verknüpften Daten zu. Bitte beachten Sie, dass einige Daten, wie z. B. Finanztransaktionen, gemäß den geltenden Gesetzen aufbewahrt werden können.",
			header: "Konto löschen bestätigen",
		},
		add: "Hinzufügen",
		addressLine1: "Adresszeile 1",
		addressLine2: "Adresszeile 2",
		appRedirectionMessage: "Sie werden zur App weitergeleitet",
		back: "Zurück",
		billing: "Abrechnung",
		buySms: "SMS kaufen",
		cancel: "Abbrechen",
		confirm: "Bestätigen",
		confirmPurchase: "Kauf bestätigen",
		connectionErrorPage: {
			body: "Verbindung zum Server fehlgeschlagen. Bitte überprüfen Sie Ihre Internetverbindung und versuchen Sie es erneut. Wenn weiterhin Probleme auftreten, wenden Sie sich an unseren Support unter https://support.luda.farm.",
			tryAgainButton: "Erneut versuchen",
		},
		country: "Land",
		create: "Erstellen",
		createAccount: "Konto erstellen",
		day: "Tag",
		days: "Tage",
		deleteAccount: "Konto löschen",
		device: {
			addDevicePage: {
				chooseNamePrompt: "Wählen Sie einen Namen für Ihr Gerät",
				selectDevicePrompt:
					"Wählen Sie das Gerät aus, das Sie hinzufügen möchten",
			},
			card: {
				connectionWarning: "Verbindung getrennt",
				planLabel: "Abonnement",
				subscriptionStatusEnded: "Beendet",
				subscriptionStatusEnds: "Endet am",
				subscriptionStatusRenews: "Verlängert sich am",
				statusLabel: "Gerätestatus",
			},
			label: "Gerät",
			logsPage: {
				timeIntervalLabel: "Zeitintervall",
				untilLabel: "Bis",
			},
			settingsPage: {
				deviceRemovalDisclaimer:
					"Durch Klicken auf Bestätigen bestätigen Sie, dass Sie dieses Gerät aus Ihrem Konto entfernen möchten und dass verbleibende Abonnements oder andere kostenpflichtige Funktionen für dieses Gerät mit sofortiger Wirkung entschädigungslos verfallen.",
				removeDeviceLabel: "Gerät entfernen",
				sharingLabel: "Freigabe",
			},
			subscriptionLabel: "Abonnement",
		},
		devices: "Geräte",
		done: "Fertig",
		edit: "Bearbeiten",
		email: "E-Mail",
		export: "Exportieren",
		farmcamSim: {
			addDevicePage: {
				header: "FarmCam SIM hinzufügen",
				scanImsiBarcodePrompt:
					"Scannen Sie den IMSI-Barcode. Wenn der Barcode nicht erkannt wird, geben Sie die IMSI manuell ein.",
			},
			card: {
				lowDataWarning: "Wenig Datenvolumen",
				outOfDataWarning: "Kein Datenvolumen mehr",
			},
			remainingDataLabel: "Verbleibendes Datenvolumen",
			settingsPage: {
				buyExtraDataButton: "Zusätzliches Datenvolumen kaufen",
				confirmExtraDataPurchaseHeader:
					"Kauf von zusätzlichem Datenvolumen bestätigen",
				extraDataPurchaseDisclaimer: (price: string): string =>
					`Bestätigen Sie Ihren Kauf von 1 GB zusätzlichem Datenvolumen für ${price} zzgl. MwSt.`,
			},
		},
		fencealarm: {
			addDevicePage: {
				header: "FenceAlarm hinzufügen",
				scanImeiBarcodePrompt:
					"Scannen Sie den IMEI-Barcode. Wenn der Barcode nicht erkannt wird, geben Sie die IMEI manuell ein.",
			},
			alarmLevelLabel: "Alarmstufe",
			card: {
				batteryLevelLabel: "Batteriestand",
				lowBatteryWarning: "Niedrige Batteriespannung",
				lowVoltageWarning: "Niedrige Zaunspannung",
			},
			settingsPage: {
				calibrationLabel: "Kalibrierung",
				expectedVoltageLabel: "Erwartete Spannung",
				voltageAlarmLabel: "Spannungsalarm",
			},
		},
		fencealarm2: {
			addDevicePage: {
				header: "FenceAlarm2 hinzufügen",
				scanImeiQrPrompt:
					"Scannen Sie den IMEI-QR-Code. Wenn der QR-Code nicht erkannt wird, geben Sie die IMEI manuell ein.",
			},
		},
		friday: "Freitag",
		language: "Sprache",
		lastActive: "Zuletzt aktiv",
		latestVersion: "Neueste Version",
		menu: "Menü",
		monday: "Montag",
		monthly: "Monatlich",
		name: "Name",
		newsletter: "Newsletter",
		newsletterUrl: "https://www.luda.farm/pages/newsletter",
		none: "Keine",
		notifications: "Benachrichtigungen",
		off: "Aus",
		on: "Ein",
		owner: "Eigentümer",
		pageNotFound: "Seite nicht gefunden",
		password: "Passwort",
		passwordResetStep1:
			"Schritt 1: Fordern Sie einen Bestätigungscode an. Wenn Sie den Code nicht im Posteingang finden, prüfen Sie Ihren Spam-Ordner. Das Anfordern eines neuen Codes ungültig macht alle zuvor ausgestellten Codes.",
		passwordResetStep2:
			"Schritt 2: Geben Sie den Bestätigungscode ein und wählen Sie ein neues Passwort.",
		phone: "Telefon",
		postalCode: "Postleitzahl",
		price: {
			monthly: (amount: number, currency: Currency): string =>
				`${amount} ${currency} pro Monat zzgl. MwSt.`,
			yearly: (amount: number, currency: Currency): string =>
				`${amount} ${currency} pro Jahr zzgl. MwSt.`,
		},
		remove: "Entfernen",
		reset: "Zurücksetzen",
		resetPassword: "Passwort zurücksetzen",
		saturday: "Samstag",
		save: "Speichern",
		sendVerificationCode: "Bestätigungscode senden",
		signalStrength: "Signalstärke",
		signIn: "Anmelden",
		signOut: "Abmelden",
		smartplug: {
			addDevicePage: {
				header: "SmartPlug hinzufügen",
				scanImeiBarcodePrompt:
					"Scannen Sie den IMEI-Barcode. Wenn der Barcode nicht erkannt wird, geben Sie die IMEI manuell ein.",
			},
			card: {
				consumptionWarning: "Verbrauch hat Schwellenwert überschritten",
				highLoadWarning: "Hohe Last",
				highTempWarning: "Hohe Temperatur",
				lowLoadWarning: "Niedrige Last",
				lowTempWarning: "Niedrige Temperatur",
				powerFailureWarning: "Stromausfall",
				powerLabel: "Strom",
			},
			consumptionLabel: "Verbrauch",
			powerLoadLabel: "Stromlast",
			settingsPage: {
				automation: {
					actionLabel: "Aktion",
					turnOff: "Ausschalten",
					turnOn: "Einschalten",
				},
				automationActionLabel: "Aktion",
				consumptionLabel: "Verbrauch",
				deviceBeepLabel: "Geräte-Signalton",
				highestExpectedLoadLabel:
					"Höchste akzeptable Last (max. 3700 W)",
				highestExpectedTempLabel:
					"Höchste akzeptable Temperatur (max. 70 °C)",
				lowestExpectedLoadLabel:
					"Niedrigste akzeptable Last (min. 0 W)",
				lowestExpectedTempLabel:
					"Niedrigste akzeptable Temperatur (min. -20 °C)",
				powerFailureNotificationsLabel: "Meldungen bei Stromausfall",
				powerLoadNotificationsLabel: "Meldungen zur Stromlast",
				temperatureNotifications: "Temperaturmeldungen",
				timer: {
					label: "Zeitschaltuhr",
					newRuleLabel: "Neue Zeitschaltregel",
				},
				thermostat: {
					above: "Über",
					below: "Unter",
					label: "Thermostat",
					newRuleLabel: "Neue Thermostatregel",
					temperatureLabel: "Temperatur (-20 - 70 °C)",
				},
			},
			temperatureLabel: "Temperatur",
		},
		smartplug2: {
			addDevicePage: {
				header: "SmartPlug2 hinzufügen",
				scanImeiQrPrompt:
					"Scannen Sie den IMEI-QR-Code. Wenn der QR-Code nicht erkannt wird, geben Sie die IMEI manuell ein.",
			},
		},
		smsPurchaseDisclaimer: (amount: number, currency: Currency): string =>
			`Durch Klicken auf Bestätigen stimmen Sie dem Kauf und der sofortigen Lieferung von 100 SMS zum Empfang von Benachrichtigungen zu. Der zu zahlende Betrag beträgt ${amount} ${currency} zzgl. MwSt. und wird auf Ihrer nächsten Rechnung ausgewiesen.`,
		sunday: "Sonntag",
		supportUrl:
			"https://support.luda.farm/de/support/solutions/14000076272",
		tags: "Tags",
		thursday: "Donnerstag",
		time: "Zeit",
		town: "Stadt",
		trial: "Testversion",
		tuesday: "Dienstag",
		update: "Aktualisieren",
		vatId: "EU-Umsatzsteuer-Identifikationsnummer",
		verificationCode: "Bestätigungscode",
		verificationCodeInstruction:
			"Wenn Sie die E-Mail mit dem Bestätigungscode nicht finden, prüfen Sie Ihren Spam-Ordner. Das Anfordern eines neuen Bestätigungscodes ungültig macht alle zuvor ausgestellten Codes.",
		verifyAccount: "Konto bestätigen",
		voltage: "Spannung",
		wednesday: "Mittwoch",
		yearly: "Jährlich",
	},
	"fr-FR": {
		account: "Compte",
		accountCreationStep1:
			"Étape 1 : Saisissez les informations de compte requises.",
		accountCreationStep2:
			"Étape 2 (facultatif) : Ajoutez des informations supplémentaires à inclure sur vos factures. Vous pouvez ignorer cette étape et les ajouter plus tard via le menu de facturation.",
		accountDeletionPage: {
			disclaimer:
				"En cliquant sur confirmer, vous acceptez d'annuler tous les abonnements actifs de ce compte sans remboursement, ainsi que la suppression immédiate de toutes les données associées. Notez que certaines données, telles que les transactions financières, peuvent être conservées conformément à la loi en vigueur.",
			header: "Confirmer la suppression du compte",
		},
		add: "Ajouter",
		addressLine1: "Adresse ligne 1",
		addressLine2: "Adresse ligne 2",
		appRedirectionMessage: "Vous allez être redirigé vers l'application",
		back: "Retour",
		billing: "Facturation",
		buySms: "Acheter des SMS",
		cancel: "Annuler",
		confirm: "Confirmer",
		confirmPurchase: "Confirmer l'achat",
		connectionErrorPage: {
			body: "La connexion au serveur a échoué. Veuillez vérifier votre connexion Internet et réessayer. Si le problème persiste, contactez notre support à l'adresse https://support.luda.farm.",
			tryAgainButton: "Réessayer",
		},
		country: "Pays",
		create: "Créer",
		createAccount: "Créer un compte",
		day: "Jour",
		days: "Jours",
		deleteAccount: "Supprimer le compte",
		device: {
			addDevicePage: {
				chooseNamePrompt: "Choisissez un nom pour votre appareil",
				selectDevicePrompt:
					"Sélectionnez l'appareil que vous souhaitez ajouter",
			},
			card: {
				connectionWarning: "Connexion perdue",
				planLabel: "Abonnement",
				subscriptionStatusEnded: "Terminé",
				subscriptionStatusEnds: "Prend fin le",
				subscriptionStatusRenews: "Se renouvelle le",
				statusLabel: "Statut de l'appareil",
			},
			label: "Appareil",
			logsPage: {
				timeIntervalLabel: "Intervalle de temps",
				untilLabel: "Jusqu'à",
			},
			settingsPage: {
				deviceRemovalDisclaimer:
					"En cliquant sur confirmer, vous reconnaissez vouloir retirer cet appareil de votre compte. Tout temps d'abonnement restant ou autre option payante associée à cet appareil sera perdu immédiatement et sans compensation.",
				removeDeviceLabel: "Retirer l'appareil",
				sharingLabel: "Partage",
			},
			subscriptionLabel: "Abonnement",
		},
		devices: "Appareils",
		done: "Terminé",
		edit: "Modifier",
		email: "E-mail",
		export: "Exporter",
		farmcamSim: {
			addDevicePage: {
				header: "Ajouter FarmCam SIM",
				scanImsiBarcodePrompt:
					"Scannez le code-barres IMSI. Si le code-barres n'est pas reconnu, saisissez l'IMSI manuellement.",
			},
			card: {
				lowDataWarning: "Données faibles",
				outOfDataWarning: "Plus de données",
			},
			remainingDataLabel: "Données restantes",
			settingsPage: {
				buyExtraDataButton: "Acheter des données recharge",
				confirmExtraDataPurchaseHeader:
					"Confirmer l'achat de données recharge",
				extraDataPurchaseDisclaimer: (price: string): string =>
					`Confirmez l'achat de 1 Go de données supplémentaires pour ${price} HT`,
			},
		},
		fencealarm: {
			addDevicePage: {
				header: "Ajouter FenceAlarm",
				scanImeiBarcodePrompt:
					"Scannez le code-barres IMEI. Si le code-barres n'est pas reconnu, saisissez l'IMEI manuellement.",
			},
			alarmLevelLabel: "Niveau d'alarme",
			card: {
				batteryLevelLabel: "Niveau de batterie",
				lowBatteryWarning: "Tension de batterie faible",
				lowVoltageWarning: "Tension de clôture faible",
			},
			settingsPage: {
				calibrationLabel: "Étalonnage",
				expectedVoltageLabel: "Tension attendue",
				voltageAlarmLabel: "Alarme de tension",
			},
		},
		fencealarm2: {
			addDevicePage: {
				header: "Ajouter FenceAlarm2",
				scanImeiQrPrompt:
					"Scannez le code QR IMEI. Si le code QR n'est pas reconnu, saisissez l'IMEI manuellement.",
			},
		},
		friday: "Vendredi",
		language: "Langue",
		lastActive: "Dernière activité",
		latestVersion: "Dernière version",
		menu: "Menu",
		monday: "Lundi",
		monthly: "Mensuel",
		name: "Nom",
		newsletter: "Bulletin d'information",
		newsletterUrl: "https://www.luda.farm/pages/newsletter",
		none: "Aucun",
		notifications: "Notifications",
		off: "Éteint",
		on: "Allumé",
		owner: "Propriétaire",
		pageNotFound: "Page non trouvée",
		password: "Mot de passe",
		passwordResetStep1:
			"Étape 1 : Demandez un code de vérification. Si vous ne le trouvez pas dans votre boîte de réception, vérifiez vos spams. Demander un nouveau code rendra les précédents caducs.",
		passwordResetStep2:
			"Étape 2 : Saisissez le code de vérification et choisissez un nouveau mot de passe.",
		phone: "Téléphone",
		postalCode: "Code postal",
		price: {
			monthly: (amount: number, currency: Currency): string =>
				`${amount} ${currency} par mois HT`,
			yearly: (amount: number, currency: Currency): string =>
				`${amount} ${currency} par an HT`,
		},
		remove: "Supprimer",
		reset: "Réinitialiser",
		resetPassword: "Réinitialiser le mot de passe",
		saturday: "Samedi",
		save: "Enregistrer",
		sendVerificationCode: "Envoyer le code de vérification",
		signalStrength: "Force du signal",
		signIn: "Connexion",
		signOut: "Déconnexion",
		smartplug: {
			addDevicePage: {
				header: "Ajouter SmartPlug",
				scanImeiBarcodePrompt:
					"Scannez le code-barres IMEI. Si le code-barres n'est pas reconnu, saisissez l'IMEI manuellement.",
			},
			card: {
				consumptionWarning: "La consommation a dépassé le seuil",
				highLoadWarning: "Charge élevée",
				highTempWarning: "Température élevée",
				lowLoadWarning: "Charge faible",
				lowTempWarning: "Température basse",
				powerFailureWarning: "Coupure de courant",
				powerLabel: "Alimentation",
			},
			consumptionLabel: "Consommation",
			powerLoadLabel: "Charge électrique",
			settingsPage: {
				automation: {
					actionLabel: "Action",
					turnOff: "Éteindre",
					turnOn: "Allumer",
				},
				automationActionLabel: "Action",
				consumptionLabel: "Consommation",
				deviceBeepLabel: "Bip de l'appareil",
				highestExpectedLoadLabel:
					"Charge maximale acceptable (max 3700 W)",
				highestExpectedTempLabel:
					"Température maximale acceptable (max 70 °C)",
				lowestExpectedLoadLabel: "Charge minimale acceptable (min 0 W)",
				lowestExpectedTempLabel:
					"Température minimale acceptable (min -20 °C)",
				powerFailureNotificationsLabel:
					"Notifications de coupure de courant",
				powerLoadNotificationsLabel:
					"Notifications de charge électrique",
				temperatureNotifications: "Notifications de température",
				timer: {
					label: "Minuteur",
					newRuleLabel: "Nouvelle règle de minuteur",
				},
				thermostat: {
					above: "Au-dessus de",
					below: "En dessous de",
					label: "Thermostat",
					newRuleLabel: "Nouvelle règle de thermostat",
					temperatureLabel: "Température (-20 - 70 °C)",
				},
			},
			temperatureLabel: "Température",
		},
		smartplug2: {
			addDevicePage: {
				header: "Ajouter SmartPlug2",
				scanImeiQrPrompt:
					"Scannez le code QR IMEI. Si le code QR n'est pas reconnu, saisissez l'IMEI manuellement.",
			},
		},
		smsPurchaseDisclaimer: (amount: number, currency: Currency): string =>
			`En cliquant sur confirmer, vous acceptez l'achat et la livraison immédiate de 100 SMS pour recevoir les notifications. Le montant à payer est de ${amount} ${currency} HT et sera inclus sur votre prochaine facture.`,
		sunday: "Dimanche",
		supportUrl:
			"https://support.luda.farm/fr/support/solutions/14000076272",
		tags: "Balises",
		thursday: "Jeudi",
		time: "Heure",
		town: "Ville",
		trial: "Essai",
		tuesday: "Mardi",
		update: "Mettre à jour",
		vatId: "Numéro de TVA intracommunautaire",
		verificationCode: "Code de vérification",
		verificationCodeInstruction:
			"Si vous ne trouvez pas l'e-mail avec le code de vérification dans votre boîte de réception, vérifiez vos spams. Demander un nouveau code rendra tous les précédents caducs.",
		verifyAccount: "Vérifier le compte",
		voltage: "Tension",
		wednesday: "Mercredi",
		yearly: "Annuel",
	},
	"sv-SE": {
		account: "Konto",
		accountCreationStep1: "Steg 1: Ange de kontouppgifter som krävs.",
		accountCreationStep2:
			"Steg 2 (valfritt): Lägg till ytterligare information som ska visas på dina fakturor. Du kan hoppa över detta steg och lägga till uppgifterna senare via faktureringsmenyn.",
		accountDeletionPage: {
			disclaimer:
				"Genom att klicka på bekräfta godkänner du att alla aktiva prenumerationer för detta konto avslutas utan återbetalning och att all data kopplad till kontot raderas omedelbart. Observera att viss data, såsom finansiella transaktioner, kan sparas i enlighet med gällande lagstiftning.",
			header: "Bekräfta radering av konto",
		},
		add: "Lägg till",
		addressLine1: "Adressrad 1",
		addressLine2: "Adressrad 2",
		appRedirectionMessage: "Du omdirigeras till appen",
		back: "Tillbaka",
		billing: "Fakturering",
		buySms: "Köp SMS",
		cancel: "Avbryt",
		confirm: "Bekräfta",
		confirmPurchase: "Bekräfta köp",
		connectionErrorPage: {
			body: "Anslutningen till servern misslyckades. Kontrollera din internetanslutning och försök igen. Om du fortfarande har problem kan du kontakta vår support på https://support.luda.farm.",
			tryAgainButton: "Försök igen",
		},
		country: "Land",
		create: "Skapa",
		createAccount: "Skapa konto",
		day: "Dag",
		days: "Dagar",
		deleteAccount: "Ta bort konto",
		device: {
			addDevicePage: {
				chooseNamePrompt: "Välj ett namn på din enhet",
				selectDevicePrompt: "Välj den enhet du vill lägga till",
			},
			card: {
				connectionWarning: "Anslutning förlorad",
				planLabel: "Abonnemang",
				subscriptionStatusEnded: "Avslutad",
				subscriptionStatusEnds: "Slutar",
				subscriptionStatusRenews: "Förnyas",
				statusLabel: "Enhetsstatus",
			},
			label: "Enhet",
			logsPage: {
				timeIntervalLabel: "Tidsintervall",
				untilLabel: "Tills",
			},
			settingsPage: {
				deviceRemovalDisclaimer:
					"Genom att klicka på bekräfta godkänner du att du vill ta bort denna enhet från ditt konto samt att eventuell återstående prenumerationstid eller andra betalda funktioner för enheten förfaller omedelbart utan ersättning.",
				removeDeviceLabel: "Ta bort enhet",
				sharingLabel: "Delning",
			},
			subscriptionLabel: "Prenumeration",
		},
		devices: "Enheter",
		done: "Klar",
		edit: "Redigera",
		email: "E-post",
		export: "Exportera",
		farmcamSim: {
			addDevicePage: {
				header: "Lägg till FarmCam SIM",
				scanImsiBarcodePrompt:
					"Skanna IMSI-streckkoden. Om streckkoden inte känns igen, skriv in IMSI manuellt.",
			},
			card: {
				lowDataWarning: "Lite surf kvar",
				outOfDataWarning: "Surfen är slut",
			},
			remainingDataLabel: "Återstående surf",
			settingsPage: {
				buyExtraDataButton: "Köp extra surf",
				confirmExtraDataPurchaseHeader: "Bekräfta köp av extra surf",
				extraDataPurchaseDisclaimer: (price: string): string =>
					`Bekräfta ditt köp av 1 GB extra surf för ${price} exkl. moms`,
			},
		},
		fencealarm: {
			addDevicePage: {
				header: "Lägg till FenceAlarm",
				scanImeiBarcodePrompt:
					"Skanna IMEI-streckkoden. Om streckkoden inte känns igen, skriv in IMEI manuellt.",
			},
			alarmLevelLabel: "Larmnivå",
			card: {
				batteryLevelLabel: "Batterinivå",
				lowBatteryWarning: "Låg batterispänning",
				lowVoltageWarning: "Låg stängselspänning",
			},
			settingsPage: {
				calibrationLabel: "Kalibrering",
				expectedVoltageLabel: "Förväntad spänning",
				voltageAlarmLabel: "Spänningslarm",
			},
		},
		fencealarm2: {
			addDevicePage: {
				header: "Lägg till FenceAlarm2",
				scanImeiQrPrompt:
					"Skanna IMEI QR-koden. Om QR-koden inte känns igen, skriv in IMEI manuellt.",
			},
		},
		friday: "Fredag",
		language: "Språk",
		lastActive: "Senast aktiv",
		latestVersion: "Senaste versionen",
		menu: "Meny",
		monday: "Måndag",
		monthly: "Månadsvis",
		name: "Namn",
		newsletter: "Nyhetsbrev",
		newsletterUrl: "https://www.luda.farm/pages/newsletter",
		none: "Ingen",
		notifications: "Notiser",
		off: "Av",
		on: "På",
		owner: "Ägare",
		pageNotFound: "Sidan hittades inte",
		password: "Lösenord",
		passwordResetStep1:
			"Steg 1: Begär en verifieringskod. Om du inte hittar koden i din inkorg, kontrollera skräppostmappen. Om du begär en ny verifieringskod blir tidigare koder ogiltiga.",
		passwordResetStep2:
			"Steg 2: Ange verifieringskoden och välj ett nytt lösenord.",
		phone: "Telefon",
		postalCode: "Postnummer",
		price: {
			monthly: (amount: number, currency: Currency): string =>
				`${amount} ${currency} per månad exkl. moms`,
			yearly: (amount: number, currency: Currency): string =>
				`${amount} ${currency} per år exkl. moms`,
		},
		remove: "Ta bort",
		reset: "Återställ",
		resetPassword: "Återställ lösenord",
		saturday: "Lördag",
		save: "Spara",
		sendVerificationCode: "Skicka verifieringskod",
		signalStrength: "Signalstyrka",
		signIn: "Logga in",
		signOut: "Logga ut",
		smartplug: {
			addDevicePage: {
				header: "Lägg till SmartPlug",
				scanImeiBarcodePrompt:
					"Skanna IMEI-streckkoden. Om streckkoden inte känns igen, skriv in IMEI manuellt.",
			},
			card: {
				consumptionWarning:
					"Förbrukningen har överskridit tröskelvärdet",
				highLoadWarning: "Hög belastning",
				highTempWarning: "Hög temperatur",
				lowLoadWarning: "Låg belastning",
				lowTempWarning: "Låg temperatur",
				powerFailureWarning: "Strömavbrott",
				powerLabel: "Ström",
			},
			consumptionLabel: "Förbrukning",
			powerLoadLabel: "Belastning",
			settingsPage: {
				automation: {
					actionLabel: "Åtgärd",
					turnOff: "Stäng av",
					turnOn: "Slå på",
				},
				automationActionLabel: "Åtgärd",
				consumptionLabel: "Förbrukning",
				deviceBeepLabel: "Ljudsignal från enhet",
				highestExpectedLoadLabel:
					"Högsta godtagbara belastning (max 3700 W)",
				highestExpectedTempLabel:
					"Högsta godtagbara temperatur (max 70 °C)",
				lowestExpectedLoadLabel:
					"Lägsta godtagbara belastning (min 0 W)",
				lowestExpectedTempLabel:
					"Lägsta godtagbara temperatur (min -20 °C)",
				powerFailureNotificationsLabel: "Notiser vid strömavbrott",
				powerLoadNotificationsLabel: "Notiser för belastning",
				temperatureNotifications: "Temperaturnotiser",
				timer: {
					label: "Timer",
					newRuleLabel: "Ny timerregel",
				},
				thermostat: {
					above: "Över",
					below: "Under",
					label: "Termostat",
					newRuleLabel: "Ny termostatregel",
					temperatureLabel: "Temperatur (-20 - 70 °C)",
				},
			},
			temperatureLabel: "Temperatur",
		},
		smartplug2: {
			addDevicePage: {
				header: "Lägg till SmartPlug2",
				scanImeiQrPrompt:
					"Skanna IMEI QR-koden. Om QR-koden inte känns igen, skriv in IMEI manuellt.",
			},
		},
		smsPurchaseDisclaimer: (amount: number, currency: Currency): string =>
			`Genom att klicka på bekräfta godkänner du köpet och omedelbar leverans av 100 SMS för att ta emot notiser. Beloppet att betala är ${amount} ${currency} exkl. moms och kommer att inkluderas på din nästa faktura.`,
		sunday: "Söndag",
		supportUrl:
			"https://support.luda.farm/sv-SE/support/solutions/14000076272",
		tags: "Taggar",
		thursday: "Torsdag",
		time: "Tid",
		town: "Ort",
		trial: "Testperiod",
		tuesday: "Tisdag",
		update: "Uppdatera",
		vatId: "Momsregistreringsnummer (VAT-nummer)",
		verificationCode: "Verifieringskod",
		verificationCodeInstruction:
			"Om du inte hittar e-postmeddelandet med verifieringskoden i din inkorg, kontrollera skräppostmappen. Om du begär en ny verifieringskod blir tidigare koder ogiltiga.",
		verifyAccount: "Verifiera konto",
		voltage: "Spänning",
		wednesday: "Onsdag",
		yearly: "Årsvis",
	},
};

export type Currency = "EUR" | "SEK";
type DateLike = Date | number | string;
type Locale = "de-DE" | "en-GB" | "fr-FR" | "sv-SE";
type TranslationSchema = typeof enGB;
